import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ReCAPTCHA from "react-google-recaptcha";
import { environment } from "../../../../utils/environment";
import {
  IAssignment,
  InviteUserModalController,
  InviteUserModalControllerDelegate,
} from "./controller";

interface State {
  isLoading: boolean;
  assignments: IAssignment[];
  selectedAssignments: number[];
  firstName: string;
  lastName: string;
  email: string;
  recaptchaToken: string;
}

interface Props {
  delegate: InviteUserModalDelegate;
}

export interface InviteUserModalDelegate {
  shouldCloseModal: () => void;
  applicationShouldLogout: () => void;
  genericError: () => void;
  didInviteUser: () => void;
}

class InviteUserModal
  extends React.Component<Props, State>
  implements InviteUserModalControllerDelegate {
  controller: InviteUserModalController;
  recaptchaRef = React.createRef<ReCAPTCHA>();

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      assignments: [],
      selectedAssignments: [],
      firstName: "",
      lastName: "",
      email: "",
      recaptchaToken: "",
    };
    this.controller = new InviteUserModalController(this);
  }

  // delegate
  didGetAssignments(assignments: IAssignment[]) {
    this.setState({
      isLoading: false,
      assignments,
    });
  }

  didInviteUser() {
    this.props.delegate.didInviteUser();
  }

  genericError() {
    this.props.delegate.genericError();
  }

  applicationShouldLogout() {
    this.props.delegate.applicationShouldLogout();
  }

  handleChangeFirstName(event: any) {
    this.setState({
      firstName: event.target.value,
    });
  }

  handleChangeLastName(event: any) {
    this.setState({
      lastName: event.target.value,
    });
  }

  handleChangeEmail(value: string) {
    this.setState({
      email: value.toLowerCase().trim(),
    });
  }

  handleChangeSelected(event: any) {
    this.setState({
      selectedAssignments: [...event.target.value],
    });
  }

  componentDidMount() {
    const token = localStorage.getItem("AccessToken")!;
    this.controller.getManagedAssignments(token);
  }

  didClickClose() {
    this.props.delegate.shouldCloseModal();
  }

  async onRecaptchaChange() {
    const recaptchaToken = this.recaptchaRef.current?.getValue() || "";
    this.setState({
      recaptchaToken,
    });
  }

  async didClickInviteUsers() {
    this.setState({
      isLoading: true,
    });
    const token = localStorage.getItem("AccessToken")!;
    const idsToAdd = this.state.selectedAssignments.map((selected) => {
      return this.state.assignments[selected].applicationId;
    });
    const { firstName, lastName, email, recaptchaToken } = this.state;
    this.controller.inviteUser(
      token,
      firstName,
      lastName,
      email,
      idsToAdd,
      recaptchaToken || ""
    );
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderForm() {
    const options = this.state.assignments.map((assignment, index) => {
      return (
        <MenuItem value={index} key={`available_assignment_${index}`}>
          <Checkbox
            checked={this.state.selectedAssignments.indexOf(index) >= 0}
          />
          {`${assignment.title} (${assignment.instrumentTitle})`}
        </MenuItem>
      );
    });

    return (
      <Box display="flex" flexDirection="column" sx={{ gap: 2 }}>
        <Typography color="text.secondary">
          Digite os dados do usuário. Se ele não tiver uma conta, um convite
          será enviado.
        </Typography>
        <TextField
          variant="outlined"
          required
          label="Nome"
          fullWidth
          onChange={(event) => this.handleChangeFirstName(event)}
        />
        <TextField
          variant="outlined"
          required
          label="Sobrenome"
          fullWidth
          onChange={(event) => this.handleChangeLastName(event)}
        />
        <TextField
          variant="outlined"
          required
          label="E-mail"
          fullWidth
          onChange={(event) => this.handleChangeEmail(event.target.value)}
        />
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">Aplicações</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            label="Aplicações"
            multiple
            value={this.state.selectedAssignments}
            renderValue={(itens) =>
              itens.map((item) => this.state.assignments[item].title).join(",")
            }
            onChange={(event) => this.handleChangeSelected(event)}
          >
            {options}
          </Select>
        </FormControl>
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <ReCAPTCHA
            ref={this.recaptchaRef}
            sitekey={environment.google.recaptchaToken}
            onChange={() => this.onRecaptchaChange()}
          />
        </Box>

      </Box>
    );
  }

  render() {
    return (
      <Dialog
        open
        onClose={() => this.didClickClose()}
        variant="modal"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle variant="modal">
          <PersonAddIcon/>
          Convidar usuário
        </DialogTitle>
        <DialogContent variant="modal">
          {this.state.isLoading ? this.renderLoading() : this.renderForm()}
        </DialogContent>
        {!this.state.isLoading && (
          <DialogActions
            variant="modal"
            sx={{ flexWrap: "wrap", gap: 1, justifyContent: "end" }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => this.didClickClose()}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={() => this.didClickInviteUsers()}
              disabled={
                this.state.email.length === 0 ||
                this.state.firstName.length === 0 ||
                this.state.lastName.length === 0 ||
                this.state.recaptchaToken.length === 0
              }
            >
              Convidar Usuário
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

export default InviteUserModal;
